import {
  PlacesBaseOnCity
} from "@/types/itinerary";
import { TourProduct } from "@/types/itinerary/explore";
import { ItineraryItemV2 } from "@/types/itinerary/itineraryV2";
import { WebbedHotel } from "@/types/webbeds";
import { create } from "zustand";
import { persist } from "zustand/middleware";

export type TravelMode = "DRIVING" | "WALKING" | "BICYCLING" | "TRANSIT";

interface ItineraryState {
  travelMode: TravelMode;
  modalData: ItineraryItemV2 | null;
  pathPoints: { lat: number | string; lng: number | string }[];
  activityModal: TourProduct | null;
  itemsDetails: PlacesBaseOnCity[];
  routes: { index: number; route: any; itemsCount: number }[];

  hotels: WebbedHotel[];
  totalHotelsCount: number;
  showAccessModal: boolean; //❌change❌
  showHotels: boolean;
  showSidebar: boolean; //❌change❌
  showActivities: boolean;
  activities: PlacesBaseOnCity[];
  nearBys: PlacesBaseOnCity[];
  nearByHotels: WebbedHotel[];
  suggestionActivities: TourProduct[];
  placesModal: PlacesBaseOnCity | null;
  userPin: {
    lat: number;
    lng: number;
  } | null;
  showHotelsFilters: boolean;
  showPlaceDetail: PlacesBaseOnCity | null;
  zoomTo: { lat: number; lng: number } | null;
  isLoadingPlace: boolean;
  hotelDetail: WebbedHotel | null;
  activeDays: number[];
  searchRadios: number;
  showSettingsModal: boolean;
}

interface ItinerarySetters {
  setRoutes: (
    routes: { index: number; route: any; itemsCount: number }[],
  ) => void;
  setTravelMode: (travelMode: TravelMode) => void;
  setSearchRadios: (searchRadios: number) => void;
  setTotalHotelsCount: (hotelsCount: number) => void;
  setHotelDetail: (hotelDetail: WebbedHotel | null) => void;
  setIsLoadingPlace: (isLoadingPlace: boolean) => void;
  setZoomTo: (zoomTo: { lat: number; lng: number } | null) => void;
  setShowPlaceDetail: (showPlaceDetail: PlacesBaseOnCity | null) => void;
  setShowHotelsFilters: (showHotelsFilters: boolean) => void;
  setShowSidebar: (showSidebar: boolean) => void;
  setUserPin: (
    userPin: {
      lat: number;
      lng: number;
    } | null,
  ) => void;
  setModalData: (data: ItineraryItemV2 | null) => void;
  setItemsDetails: (items: PlacesBaseOnCity[]) => void;
  setHotels: (hotels: WebbedHotel[]) => void;
  setShowHotels: (s: boolean) => void;
  setShowAccessModal: (s: boolean) => void;
  setActivities: (s: PlacesBaseOnCity[]) => void;
  setNearBys: (s: PlacesBaseOnCity[]) => void;
  setNearByHotels: (s: WebbedHotel[]) => void;
  setSuggestionActivities: (s: TourProduct[]) => void;
  setActivityModal: (s: TourProduct | null) => void;
  setShowActivities: (s: boolean) => void;
  setPlacesModal: (s: PlacesBaseOnCity | null) => void;
  setActiveDays: (s: number[]) => void;
  setShowSettingsModal: (s: boolean) => void;
  updateRoutes: (
    newRoutes: {
      index: number;
      route: any; // Replace with actual route data type if known
      itemsCount: number;
    }[],
  ) => void;
}

const initialState: ItineraryState = {
  travelMode: "DRIVING",
  routes: [],
  totalHotelsCount: 0,
  pathPoints: [],
  showAccessModal: false,
  placesModal: null,
  modalData: null,
  itemsDetails: [],
  hotels: [],
  showHotels: true,
  activities: [],
  nearBys: [],
  nearByHotels: [],
  suggestionActivities: [],
  showActivities: false,
  activityModal: null,
  userPin: null,
  showSidebar: false,
  showHotelsFilters: false,
  showPlaceDetail: null,
  zoomTo: null,
  isLoadingPlace: false,
  hotelDetail: null,
  activeDays: [],
  searchRadios: 1000,
  showSettingsModal: false,
};

export const useItineraryMapStore = create<ItineraryState & ItinerarySetters>()(
  persist(
    (set) => ({
      ...initialState,
      setTotalHotelsCount: (hotelsCount) =>
        set({ totalHotelsCount: hotelsCount }),
      updateRoutes: (newRoutes) =>
        set((state) => {
          const updatedRoutes = [...state.routes];

          for (const newRoute of newRoutes) {
            updatedRoutes[newRoute.index] = newRoute;
          }

          return { routes: updatedRoutes };
        }),
      setRoutes: (routes) => set({ routes }),
      setTravelMode: (travelMode) => set({ travelMode }),
      setSearchRadios: (searchRadios: number) => set({ searchRadios }),
      setShowAccessModal: (showAccessModal) => set({ showAccessModal }),
      setActiveDays: (activeDays) => set({ activeDays }),
      setHotelDetail: (hotelDetail) => set({ hotelDetail }),
      setIsLoadingPlace: (isLoadingPlace) => set({ isLoadingPlace }),
      setZoomTo: (zoomTo) => set({ zoomTo }),
      setShowPlaceDetail: (showPlaceDetail) => set({ showPlaceDetail }),
      setShowHotelsFilters: (showHotelsFilters) => set({ showHotelsFilters }),
      setShowSidebar: (showSidebar) => set({ showSidebar }),
      setUserPin: (userPin) => set({ userPin }),
      setPlacesModal: (placesModal) => set({ placesModal }),
      setActivityModal: (activityModal) => set({ activityModal }),
      setActivities: (activities) => set({ activities }),
      setNearBys: (nearBys) => set({ nearBys }),
      setNearByHotels: (nearByHotels) => set({ nearByHotels }),
      setSuggestionActivities: (suggestionActivities) =>
        set({ suggestionActivities }),
      setModalData: (data) => set({ modalData: data }),
      setItemsDetails: (items) => set({ itemsDetails: items }),
      setHotels: (hotels) => set({ hotels }),
      setShowHotels: (showHotels) => set({ showHotels }),
      setShowActivities: (showActivities) => set({ showActivities }),
      setShowSettingsModal: (showSettingsModal) => set({ showSettingsModal }),
      resetItinerary: () => set(initialState),
    }),
    {
      name: "itinerary-store",
      version: 1,
      partialize: (state) => ({ itemsDetails: state.itemsDetails }), // persist only itemsDetails
    },
  ),
);
